import { Action, ThunkAction, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { profileApi } from '../services/ProfileService/profileApi';
import { configurationApi } from '../services/ConfigurationService/configurationApi';
import { encounterApi } from '../services/EncounterService/encounterApi';
import { registrationApi } from '../services/RegistrationService/registrationApi';
import { caseManagementApi } from '../services/CaseManagementService/caseManagementApi';
import { refundApi } from '../services/RefundService/refundApi';
import { writeoffApi } from '../services/WriteoffService/writeoffApi';
import { contractApi } from '../services/ContractService/contractApi';
import { accountApi } from '../services/AccountService/accountApi';
import { appealsApi } from '../services/AppealsService/appealsApi';
import { calculatorApi } from '../services/CalculatorService/calculatorApi';
import { laborManagementApi, laborManagementReducer } from '../services/LaborManagementService';
import { medNecApi, medNecCaseReducer } from '../services/MedNecService';
import { verificationsApi, verificationReducer, preQuoteReducer } from '../modules/financialClearance/data';
import { agentsApi, agentsVerificationsApi } from '../modules/agents/data';
import { claimsApi } from '../modules/claims/data';
import { denialsApi } from '../modules/denials/data';
import { revenueRecognitionApi } from '../modules/revenueRecognition/data';
import centerReducer from '../services/ConfigurationService/centerSlice';
import registrationReducer from '../services/RegistrationService/registrationSlice/';
import encounterReducer from '../services/EncounterService/slice';
import policyReducer from '../services/CaseManagementService/policySlice';
import caseManageReducer from '../services/CaseManagementService/caseManageSlice';
import refundReducer from '../services/RefundService/slice';
import writeoffReducer from '../services/WriteoffService/slice';
import contractReducer from '../services/ContractService/contractSlice';
import contractEstimateReducer from '../services/ContractService/contractEstimateSlice';
import appealReducer from '../services/AppealsService/appealSlice/';
import accountReducer from '../services/AccountService/accountSlice';
import remitReducer from '../services/AccountService/remitSlice';

const rootReducer = {
  center: centerReducer,
  registration: registrationReducer,
  encounter: encounterReducer,
  policy: policyReducer,
  caseManage: caseManageReducer,
  refund: refundReducer,
  writeoff: writeoffReducer,
  contract: contractReducer,
  contractEstimate: contractEstimateReducer,
  appeal: appealReducer,
  account: accountReducer,
  remit: remitReducer,
  laborManagement: laborManagementReducer,
  medNecCase: medNecCaseReducer,
  verification: verificationReducer,
  preQuote: preQuoteReducer,
  [profileApi.reducerPath]: profileApi.reducer,
  [configurationApi.reducerPath]: configurationApi.reducer,
  [encounterApi.reducerPath]: encounterApi.reducer,
  [registrationApi.reducerPath]: registrationApi.reducer,
  [caseManagementApi.reducerPath]: caseManagementApi.reducer,
  [refundApi.reducerPath]: refundApi.reducer,
  [writeoffApi.reducerPath]: writeoffApi.reducer,
  [contractApi.reducerPath]: contractApi.reducer,
  [accountApi.reducerPath]: accountApi.reducer,
  [appealsApi.reducerPath]: appealsApi.reducer,
  [calculatorApi.reducerPath]: calculatorApi.reducer,
  [laborManagementApi.reducerPath]: laborManagementApi.reducer,
  [medNecApi.reducerPath]: medNecApi.reducer,
  [verificationsApi.reducerPath]: verificationsApi.reducer,
  [agentsApi.reducerPath]: agentsApi.reducer,
  [agentsVerificationsApi.reducerPath]: agentsVerificationsApi.reducer,
  [claimsApi.reducerPath]: claimsApi.reducer,
  [denialsApi.reducerPath]: denialsApi.reducer,
  [revenueRecognitionApi.reducerPath]: revenueRecognitionApi.reducer,
};

const apiMiddleware = [
  profileApi.middleware,
  configurationApi.middleware,
  encounterApi.middleware,
  registrationApi.middleware,
  caseManagementApi.middleware,
  refundApi.middleware,
  writeoffApi.middleware,
  contractApi.middleware,
  accountApi.middleware,
  appealsApi.middleware,
  calculatorApi.middleware,
  laborManagementApi.middleware,
  medNecApi.middleware,
  verificationsApi.middleware,
  agentsApi.middleware,
  agentsVerificationsApi.middleware,
  claimsApi.middleware,
  denialsApi.middleware,
  revenueRecognitionApi.middleware,
];

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // RTK Query API cache paths that may contain Blob objects, and non-serializable payloads.
        ignoredPaths: [
          'contractApi.mutations',
          'contractApi.queries',
          'configurationApi.mutations',
          'configurationApi.queries',
          'laborManagementApi.mutations',
          'laborManagementApi.queries',
        ],
        ignoredActionsPaths: ['payload.data', 'meta.baseQueryMeta'],
      },
    }).concat(...apiMiddleware),
});

setupListeners(store.dispatch);
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
